import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Camera,
  ImageUp,
  Keyboard,
  QrCode,
  ShieldCheck,
  CalendarCheck,
  MapPin,
  Calendar,
  Clock,
  Building2,
  AlertCircle,
  CalendarRange,
  Tent
} from 'lucide-react';
import jsQR from 'jsqr';
import { useLocation } from 'react-router-dom';
import '../styles/scan-qr.css';
import { lookupMemberByQrReference } from '../../src/api/users';
import { resolveQrReference } from '../../src/utils/qr';
import { API_BASE_URL } from '../../src/config';
import { resolveBackendAssetUrl } from '../../src/utils/assetUrl';

const initialStatus = {
  message: '',
  detail: '',
};

const tryParseJson = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const parseMemberQrPayload = (rawValue) => {
  if (!rawValue) {
    return null;
  }

  const attempts = [rawValue];

  try {
    attempts.push(decodeURIComponent(rawValue));
  } catch (error) {
    // ignore decoding errors
  }

  try {
    attempts.push(atob(rawValue));
  } catch (error) {
    // ignore base64 decode errors
  }

  let parsed = null;
  for (const attempt of attempts) {
    parsed = tryParseJson(attempt);
    if (parsed) {
      break;
    }
  }

  if (!parsed) {
    return null;
  }

  const type = typeof parsed.type === 'string' ? parsed.type.toLowerCase() : '';
  if (!['alu_member_id', 'alu-member', 'member-card'].includes(type)) {
    return null;
  }

  const memberId = typeof parsed.memberId === 'string'
    ? parsed.memberId
    : (typeof parsed.idNumber === 'string' ? parsed.idNumber : null);
  if (!memberId) {
    return null;
  }

  const shortId = typeof parsed.shortId === 'string'
    ? parsed.shortId
    : (memberId.includes('-') ? memberId.split('-').pop() : memberId);

  const userId = typeof parsed.userId === 'number' ? parsed.userId : (typeof parsed.userId === 'string' ? parseInt(parsed.userId, 10) : null);

  const company = typeof parsed.company === 'string' ? parsed.company : '';
  const union = typeof parsed.union === 'string'
    ? parsed.union
    : (typeof parsed.unionName === 'string' ? parsed.unionName : '');
  const fullName = typeof parsed.fullName === 'string'
    ? parsed.fullName
    : (typeof parsed.name === 'string' ? parsed.name : '');
  const photo = typeof parsed.photo === 'string' && parsed.photo.trim()
    ? parsed.photo
    : (typeof parsed.profilePhoto === 'string' && parsed.profilePhoto.trim() ? parsed.profilePhoto : null);
  const qrReference = typeof parsed.qrReference === 'string' ? parsed.qrReference : null;

  return {
    memberId,
    shortId,
    userId,
    company: company || '—',
    union: union || '—',
    name: fullName || 'LaborConnect Member',
    photo: photo || null,
    qrReference: qrReference || null,
  };
};

const QR_LOOKUP_QUERY_KEYS = ['ref', 'reference', 'qrReference', 'memberId', 'memberID', 'token', 'id'];

const normalizeLookupReference = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  const normalized = value.trim();
  if (!normalized || normalized.length > 160) {
    return '';
  }

  return normalized;
};

const resolveReferenceFromSearchParams = (searchParams) => {
  for (const key of QR_LOOKUP_QUERY_KEYS) {
    const candidate = normalizeLookupReference(searchParams.get(key) || '');
    if (candidate) {
      return candidate;
    }
  }
  return '';
};

const parseAsUrl = (value) => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) {
    return null;
  }

  try {
    return new URL(trimmed);
  } catch (error) {
    // continue and try relative URL resolution below
  }

  const fallbackOrigin = typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'http://localhost';

  try {
    return new URL(trimmed, fallbackOrigin);
  } catch (error) {
    return null;
  }
};

const resolveLookupReferenceFromRawValue = (rawValue) => {
  const normalizedRaw = normalizeLookupReference(rawValue);
  if (!normalizedRaw) {
    return '';
  }

  const parseAttempts = [normalizedRaw];
  try {
    parseAttempts.push(decodeURIComponent(normalizedRaw));
  } catch (error) {
    // ignore invalid encoded values
  }

  for (const candidate of parseAttempts) {
    const url = parseAsUrl(candidate);
    if (url) {
      const fromSearch = resolveReferenceFromSearchParams(url.searchParams);
      if (fromSearch) {
        return fromSearch;
      }
    }

    if (candidate.startsWith('?') || (!candidate.includes('://') && candidate.includes('='))) {
      const queryParams = new URLSearchParams(candidate.startsWith('?') ? candidate : `?${candidate}`);
      const fromQueryString = resolveReferenceFromSearchParams(queryParams);
      if (fromQueryString) {
        return fromQueryString;
      }
    }
  }

  return '';
};

const getInitials = (input) => {
  if (!input) {
    return 'LC';
  }
  const parts = input.split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return 'LC';
  }
  const initials = parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('');
  return initials || 'LC';
};

const formatTime = (timeString) => {
  if (!timeString) return '—';
  const match = String(timeString).match(/^(\d{2}):(\d{2})/);
  if (match) {
    const hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes} ${period}`;
  }
  return String(timeString);
};

const ACTIVE_EVENT_STATUSES = new Set([
  'registered',
  'attended',
  'attending',
  'confirmed',
  'approved',
  'active',
  'pending',
]);

const extractNewsCollection = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== 'object') {
    return [];
  }

  if (Array.isArray(payload.items)) {
    return payload.items;
  }

  if (Array.isArray(payload.news)) {
    return payload.news;
  }

  if (Array.isArray(payload.data)) {
    return payload.data;
  }

  return [];
};

const isEventRegisteredForMember = (eventItem) => {
  if (!eventItem || typeof eventItem !== 'object') {
    return false;
  }

  if (Boolean(eventItem.isUserAttending)) {
    return true;
  }

  const normalizedStatus = String(eventItem.userAttendanceStatus ?? '')
    .trim()
    .toLowerCase();

  return ACTIVE_EVENT_STATUSES.has(normalizedStatus);
};

const decodeJsQrFromCanvas = (canvas) => {
  const context = canvas?.getContext('2d', { willReadFrequently: true });
  if (!context) return null;

  const readAndDecode = (targetCanvas) => {
    const targetContext = targetCanvas.getContext('2d', { willReadFrequently: true });
    if (!targetContext) return null;
    const imageData = targetContext.getImageData(0, 0, targetCanvas.width, targetCanvas.height);

    const attempts = [
      { inversionAttempts: 'attemptBoth' },
      { inversionAttempts: 'invertFirst' },
      { inversionAttempts: 'onlyInvert' },
    ];

    for (const options of attempts) {
      const decoded = jsQR(imageData.data, imageData.width, imageData.height, options);
      if (decoded?.data) {
        return decoded.data;
      }
    }
    return null;
  };

  const directResult = readAndDecode(canvas);
  if (directResult) {
    return directResult;
  }

  const scales = [0.75, 0.5, 0.35];
  for (const scale of scales) {
    const scaledWidth = Math.max(120, Math.floor(canvas.width * scale));
    const scaledHeight = Math.max(120, Math.floor(canvas.height * scale));
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = scaledWidth;
    tempCanvas.height = scaledHeight;
    const tempContext = tempCanvas.getContext('2d', { willReadFrequently: true });
    if (!tempContext) {
      // eslint-disable-next-line no-continue
      continue;
    }
    tempContext.drawImage(canvas, 0, 0, scaledWidth, scaledHeight);
    const scaledResult = readAndDecode(tempCanvas);
    if (scaledResult) {
      return scaledResult;
    }
  }

  return null;
};

export default function ScanQrPage() {
  const location = useLocation();
  const [mode, setMode] = useState('scan');
  const [result, setResult] = useState('');
  const [status, setStatus] = useState('idle');
  const [feedback, setFeedback] = useState(initialStatus);
  const [previewSrc, setPreviewSrc] = useState('');
  const [cameraActive, setCameraActive] = useState(true);
  const [isMirrored, setIsMirrored] = useState(false);
  const [memberInfo, setMemberInfo] = useState(null);
  const [lastUploadSnapshot, setLastUploadSnapshot] = useState(null);
  const [lastResultSource, setLastResultSource] = useState(null);
  const [activeBookings, setActiveBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [bookingsError, setBookingsError] = useState(null);
  const [activeEvents, setActiveEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventsError, setEventsError] = useState(null);
  const [referenceValue, setReferenceValue] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const frameIntervalRef = useRef(null);
  const detectorRef = useRef(null);
  const [detectorSupported, setDetectorSupported] = useState(false);
  const statusRef = useRef('idle');
  const initialLookupRef = useRef('');

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    const canUseDetector = typeof window !== 'undefined' && 'BarcodeDetector' in window;
    if (canUseDetector) {
      try {
        detectorRef.current = new window.BarcodeDetector({ formats: ['qr_code'] });
        setDetectorSupported(true);
      } catch (error) {
        console.error('Failed to initialise BarcodeDetector', error);
        setFeedback({ message: 'QR scanning is unavailable.', detail: 'Try updating your browser or upload an image instead.' });
      }
    } else {
      setFeedback({ message: 'Running fallback QR decoder.', detail: 'Scanning may be slower on this browser; uploads remain available.' });
    }

    return () => {
      if (previewSrc) {
        window.URL.revokeObjectURL(previewSrc);
      }
    };
  }, [previewSrc]);

  const fetchActiveBookings = useCallback(async (userId) => {
    setLoadingBookings(true);
    setBookingsError(null);
    setActiveBookings([]);

    try {
      // Direct booking fetch using userId from QR code
      const bookingsRes = await fetch(
        `${API_BASE_URL}/api/my-bookings?userId=${userId}`
      );

      if (!bookingsRes.ok) {
        throw new Error('Failed to load bookings');
      }

      const bookingsData = await bookingsRes.json();

      // Filter for active bookings (pending or approved)
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const active = bookingsData.bookings.filter(booking => {
        const bookingDate = new Date(booking.bookingDate);
        const isUpcoming = bookingDate >= today;
        const isActive = ['pending', 'approved'].includes(booking.status);
        return isUpcoming && isActive;
      });

      setActiveBookings(active);
    } catch (err) {
      console.error('Error fetching active bookings:', err);
      setBookingsError(err.message || 'Unable to load active bookings');
    } finally {
      setLoadingBookings(false);
    }
  }, []);

  const fetchActiveEvents = useCallback(async (userId) => {
    setLoadingEvents(true);
    setEventsError(null);
    setActiveEvents([]);

    try {
      const eventsRes = await fetch(`${API_BASE_URL}/api/news?userId=${userId}`);
      const eventsPayload = await eventsRes.json();
      if (!eventsRes.ok) {
        throw new Error(eventsPayload?.message || 'Failed to load events');
      }

      const eventsData = extractNewsCollection(eventsPayload);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const active = eventsData.filter(evt => {
        if (!isEventRegisteredForMember(evt)) return false;
        if (!evt.endAt && !evt.startAt && !evt.publishedAt) return true;
        
        const endDate = new Date(evt.endAt || evt.startAt || evt.publishedAt);
        if (Number.isNaN(endDate.getTime())) {
          return true;
        }
        return endDate >= today;
      });

      setActiveEvents(active);
    } catch (err) {
      console.error('Error fetching active events:', err);
      setEventsError(err.message || 'Unable to load active events');
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  const mapLookupMember = useCallback((member, fallbackReference) => {
    const resolvedPhoto = resolveBackendAssetUrl(member.photo, { apiBaseUrl: API_BASE_URL }) ?? null;
    const resolvedReference = member.qrReference ?? resolveQrReference({ qrReference: fallbackReference });
    const normalizedUserId = Number(member.userId);
    const userId = Number.isFinite(normalizedUserId) && normalizedUserId > 0 ? normalizedUserId : null;

    return {
      memberId: member.memberId,
      userId,
      company: member.company ?? '—',
      union: member.union ?? '—',
      name: member.name ?? 'LaborConnect Member',
      photo: resolvedPhoto,
      qrReference: resolvedReference,
    };
  }, []);

  const lookupMemberFromReference = useCallback(async (reference, {
    source = 'reference',
    verifyingMessage = 'Checking QR reference...',
    verifyingDetail = '',
    successMessage = 'QR reference verified.',
    successDetail = 'Member details matched the reference provided.',
    notFoundMessage = 'QR reference not found.',
    notFoundDetail = 'Check the value and try again.',
    invalidMessage = 'Invalid QR verification link.',
    invalidDetail = 'The scanned value does not contain a valid reference.',
    errorMessage = 'Unable to verify QR reference.',
    errorDetail = 'Please confirm the reference and try again.',
  } = {}) => {
    const normalizedReference = normalizeLookupReference(reference);

    setLastResultSource(source);

    if (!normalizedReference) {
      setMemberInfo(null);
      setResult('');
      setFeedback({ message: invalidMessage, detail: invalidDetail });
      if (source === 'upload') {
        setLastUploadSnapshot({
          memberInfo: null,
          result: '',
          feedback: { message: invalidMessage, detail: invalidDetail },
        });
      }
      return false;
    }

    setMemberInfo(null);
    setLookupLoading(true);
    setResult(normalizedReference);
    setFeedback({ message: verifyingMessage, detail: verifyingDetail });

    try {
      const { data } = await lookupMemberByQrReference(normalizedReference);
      if (data?.member) {
        const mappedMember = mapLookupMember(data.member, normalizedReference);
        const resolvedResult = mappedMember.qrReference ?? normalizedReference;
        const nextFeedback = { message: successMessage, detail: successDetail };

        setMemberInfo(mappedMember);
        setResult(resolvedResult);
        setFeedback(nextFeedback);

        if (mappedMember.userId) {
          fetchActiveBookings(mappedMember.userId);
          fetchActiveEvents(mappedMember.userId);
        }

        if (source === 'upload') {
          setLastUploadSnapshot({
            memberInfo: mappedMember,
            result: resolvedResult,
            feedback: nextFeedback,
          });
        }

        return true;
      }

      setMemberInfo(null);
      setResult(normalizedReference);
      setFeedback({ message: notFoundMessage, detail: notFoundDetail });
      if (source === 'upload') {
        setLastUploadSnapshot({
          memberInfo: null,
          result: normalizedReference,
          feedback: { message: notFoundMessage, detail: notFoundDetail },
        });
      }
      return false;
    } catch (error) {
      console.error('QR reference lookup failed', error);
      const apiMessage = error?.response?.data?.message;
      const resolvedDetail = apiMessage || errorDetail;

      setMemberInfo(null);
      setResult(normalizedReference);
      setFeedback({ message: errorMessage, detail: resolvedDetail });
      if (source === 'upload') {
        setLastUploadSnapshot({
          memberInfo: null,
          result: normalizedReference,
          feedback: { message: errorMessage, detail: resolvedDetail },
        });
      }
      return false;
    } finally {
      setLookupLoading(false);
    }
  }, [fetchActiveBookings, fetchActiveEvents, mapLookupMember]);

  const stopScanner = useCallback((nextStatus) => {
    if (frameIntervalRef.current) {
      window.clearInterval(frameIntervalRef.current);
      frameIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.onloadedmetadata = null;
    }
    if (typeof nextStatus === 'string') {
      statusRef.current = nextStatus;
      setStatus(nextStatus);
    }
  }, []);

  const handleDetectedValue = useCallback((value, source = 'scan') => {
    if (!value) {
      return;
    }
    const trimmed = value.trim();
    setResult(trimmed);
    setLastResultSource(source);

    const linkReference = resolveLookupReferenceFromRawValue(trimmed);
    if (linkReference) {
      lookupMemberFromReference(linkReference, {
        source,
        verifyingMessage: 'Verification link detected.',
        verifyingDetail: 'Checking member details from QR link...',
        successMessage: 'Verification link matched.',
        successDetail: 'Member details loaded from the QR link.',
        notFoundMessage: 'QR verification link is invalid or not found.',
        notFoundDetail: 'Please generate a new QR from the member digital ID.',
        invalidMessage: 'Invalid LaborConnect QR link.',
        invalidDetail: 'Use a QR link generated by LaborConnect Digital ID.',
        errorMessage: 'Unable to verify QR link.',
        errorDetail: 'Please try again in a moment.',
      });
      return;
    }

    const parsed = parseMemberQrPayload(trimmed);
    if (parsed) {
      const nextFeedback = { message: 'LaborConnect membership QR detected.', detail: 'Verify the member details below.' };
      setMemberInfo(parsed);
      setFeedback(nextFeedback);

      // Fetch active bookings & events for this member
      if (parsed.userId) {
        fetchActiveBookings(parsed.userId);
        fetchActiveEvents(parsed.userId);
      }

      if (source === 'upload') {
        setLastUploadSnapshot({ memberInfo: parsed, result: trimmed, feedback: nextFeedback });
      }
    } else {
      setMemberInfo(null);
      const nextFeedback = {
        message: 'Unsupported QR content.',
        detail: 'Use a LaborConnect QR verification link or member QR code.',
      };
      setFeedback(nextFeedback);
      if (source === 'upload') {
        setLastUploadSnapshot({ memberInfo: null, result: trimmed, feedback: nextFeedback });
      }
    }
  }, [fetchActiveBookings, fetchActiveEvents, lookupMemberFromReference]);

  const detectFromCanvas = useCallback(async () => {
    if (!canvasRef.current) {
      return;
    }
    try {
      let detectedValue = null;
      if (detectorSupported && detectorRef.current) {
        const detection = await detectorRef.current.detect(canvasRef.current);
        if (detection.length > 0) {
          detectedValue = detection[0].rawValue || detection[0].value || '';
        }
      }

      if (!detectedValue && canvasRef.current) {
        detectedValue = decodeJsQrFromCanvas(canvasRef.current);
      }

      if (detectedValue) {
        handleDetectedValue(detectedValue, 'scan');
        setCameraActive(false);
        stopScanner('detected');
      }
    } catch (error) {
      console.error('Detection error', error);
    }
  }, [detectorSupported, handleDetectedValue, stopScanner]);

  const startScanner = useCallback(async () => {
    if (statusRef.current === 'scanning' || statusRef.current === 'preparing') {
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setFeedback({ message: 'Camera access is not available on this device.', detail: 'Upload an image of the QR code instead.' });
      statusRef.current = 'error';
      setStatus('error');
      return;
    }

    setResult('');
    setMemberInfo(null);
    setFeedback(detectorSupported ? { message: '', detail: '' } : { message: 'Live scanning needs a newer browser.', detail: 'Upload an image if the camera cannot read the code.' });
    setLastResultSource(null);
    statusRef.current = 'preparing';
    setStatus('preparing');

    try {
      const scanConstraintCandidates = [
        {
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            advanced: [{ focusMode: 'continuous' }],
          },
        },
        {
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
            advanced: [{ focusMode: 'continuous' }],
          },
        },
        {
          audio: false,
          video: { facingMode: 'environment' },
        },
      ];

      let stream = null;
      let streamError = null;

      for (const candidateConstraints of scanConstraintCandidates) {
        try {
          stream = await navigator.mediaDevices.getUserMedia(candidateConstraints);
          break;
        } catch (constraintError) {
          streamError = constraintError;
        }
      }

      if (!stream) {
        throw streamError || new Error('Unable to initialize camera stream.');
      }

      const videoTrack = stream.getVideoTracks?.()[0];
      if (videoTrack?.applyConstraints) {
        try {
          await videoTrack.applyConstraints({ advanced: [{ focusMode: 'continuous' }] });
        } catch (focusError) {
          // ignore unsupported focus constraints
        }
      }

      streamRef.current = stream;

      if (videoRef.current) {
        const video = videoRef.current;
        const attemptPlay = async () => {
          try {
            await video.play();
          } catch (err) {
            console.error('Video play error', err);
            setFeedback({ message: 'Unable to start the preview.', detail: 'Ensure no other application is using the camera and try again.' });
            stopScanner('error');
          }
        };

        video.srcObject = stream;
        if (video.readyState >= 2) {
          attemptPlay();
        } else {
          video.onloadedmetadata = attemptPlay;
        }
      }

      statusRef.current = 'scanning';
      setStatus('scanning');

      frameIntervalRef.current = window.setInterval(() => {
        if (!videoRef.current || videoRef.current.readyState !== 4 || !canvasRef.current) {
          return;
        }

        const canvas = canvasRef.current;
        const video = videoRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) {
          return;
        }
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        detectFromCanvas();
      }, 800);
    } catch (error) {
      console.error('Camera start error', error);
      setFeedback({ message: 'Unable to access the camera.', detail: 'Check browser permissions or upload a QR code image instead.' });
      stopScanner('error');
    }
  }, [detectFromCanvas, detectorSupported, stopScanner]);

  useEffect(() => {
    if (mode !== 'scan') {
      stopScanner('idle');
      return undefined;
    }

    if (cameraActive) {
      startScanner();
      return () => {
        stopScanner();
      };
    }

    stopScanner();
    return undefined;
  }, [cameraActive, mode, startScanner, stopScanner]);

  const handleUpload = async (event) => {
    const inputElement = event.target;
    const { files } = inputElement;
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    if (previewSrc) {
      window.URL.revokeObjectURL(previewSrc);
    }
    const objectUrl = window.URL.createObjectURL(file);
    setPreviewSrc(objectUrl);
    setResult('');
    setMemberInfo(null);
    setFeedback(initialStatus);
    setLastResultSource(null);

    if (!detectorSupported && !jsQR) {
      setFeedback({ message: 'QR decoding is unavailable in this browser.', detail: 'Open this page in a Chromium-based browser for best support.' });
      return;
    }

    if (!window.createImageBitmap && !jsQR) {
      setFeedback({ message: 'Image decoding is not supported in this browser.', detail: 'Try Chrome or Edge for the upload reader.' });
      return;
    }

    try {
      if (!canvasRef.current) {
        return;
      }
      let bitmap = null;
      if (window.createImageBitmap) {
        bitmap = await window.createImageBitmap(file);
      } else {
        const image = await new Promise((resolve, reject) => {
          const tag = new Image();
          tag.onload = () => resolve(tag);
          tag.onerror = reject;
          tag.src = objectUrl;
        });
        bitmap = image;
      }

      const canvas = canvasRef.current;
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) {
        setFeedback({ message: 'Unable to read the uploaded image.', detail: 'Try reloading the page and uploading again.' });
        return;
      }
      context.drawImage(bitmap, 0, 0);

      let decodedValue = null;

      if (detectorSupported && detectorRef.current) {
        const detection = await detectorRef.current.detect(canvas);
        if (detection.length > 0) {
          decodedValue = detection[0].rawValue || detection[0].value || '';
        }
      }

      if (!decodedValue) {
        decodedValue = decodeJsQrFromCanvas(canvas);
      }

      if (decodedValue) {
        handleDetectedValue(decodedValue, 'upload');
        setStatus('detected');
      } else {
        setMemberInfo(null);
        setFeedback({ message: 'No QR code detected in the image.', detail: 'Try a clearer image or different lighting.' });
        setLastUploadSnapshot(null);
        setLastResultSource(null);
      }
    } catch (error) {
      console.error('Upload detection error', error);
      setFeedback({ message: 'Could not read the uploaded image.', detail: 'Ensure the file is a clear QR code.' });
      setLastUploadSnapshot(null);
      setLastResultSource(null);
    }

    inputElement.value = '';
  };

  const handleReferenceLookup = async (event) => {
    event.preventDefault();
    const trimmed = referenceValue.trim();
    if (!trimmed) {
      setMemberInfo(null);
      setResult('');
      setFeedback({ message: 'Enter a QR reference number.', detail: '' });
      setLastResultSource('reference');
      return;
    }

    await lookupMemberFromReference(trimmed, {
      source: 'reference',
      verifyingMessage: 'Checking QR reference...',
      verifyingDetail: '',
      successMessage: 'QR reference verified.',
      successDetail: 'Member details matched the reference provided.',
      notFoundMessage: 'QR reference not found.',
      notFoundDetail: 'Check the number and try again.',
      invalidMessage: 'Invalid QR reference.',
      invalidDetail: 'Enter a valid QR reference value.',
      errorMessage: 'Unable to verify QR reference.',
      errorDetail: 'Please confirm the reference number and try again.',
    });
  };

  useEffect(() => {
    const queryReference = resolveReferenceFromSearchParams(new URLSearchParams(location.search || ''));
    if (!queryReference) {
      initialLookupRef.current = '';
      return;
    }

    if (initialLookupRef.current === queryReference) {
      return;
    }

    initialLookupRef.current = queryReference;
    stopScanner('idle');
    setCameraActive(false);
    setMode('reference');
    setReferenceValue(queryReference);

    lookupMemberFromReference(queryReference, {
      source: 'link',
      verifyingMessage: 'Opening verification link...',
      verifyingDetail: 'Checking member details from URL parameter.',
      successMessage: 'Verification link matched.',
      successDetail: 'Member details loaded from the QR link.',
      notFoundMessage: 'QR link is invalid or data not found.',
      notFoundDetail: 'Please scan a valid QR generated from Digital ID.',
      invalidMessage: 'Invalid QR verification link.',
      invalidDetail: 'Missing or malformed reference in URL.',
      errorMessage: 'Unable to process verification link.',
      errorDetail: 'Please refresh and try again.',
    });
  }, [location.search, lookupMemberFromReference, stopScanner]);

  const resetView = () => {
    setResult('');
    setMemberInfo(null);
    setFeedback(initialStatus);
    setLastResultSource(null);
    setActiveBookings([]);
    setLoadingBookings(false);
    setBookingsError(null);
    setActiveEvents([]);
    setLoadingEvents(false);
    setEventsError(null);
    setReferenceValue('');
    setLookupLoading(false);
    if (mode === 'upload') {
      setLastUploadSnapshot(null);
    }
    if (mode === 'scan') {
      statusRef.current = 'idle';
      setStatus('idle');
      setCameraActive(true);
    }
    if (mode === 'upload') {
      if (previewSrc) {
        window.URL.revokeObjectURL(previewSrc);
        setPreviewSrc('');
      }
    }
  };

  const clearScanResult = useCallback(() => {
    setResult('');
    setMemberInfo(null);
    setFeedback(initialStatus);
    setLastResultSource(null);
    setActiveBookings([]);
    setLoadingBookings(false);
    setBookingsError(null);
    setActiveEvents([]);
    setLoadingEvents(false);
    setEventsError(null);
    if (mode === 'scan') {
      statusRef.current = 'idle';
      setStatus('idle');
      setCameraActive(true);
    }
  }, [mode]);

  useEffect(() => {
    if (mode !== 'upload') {
      return;
    }
    if (lastUploadSnapshot) {
      setMemberInfo(lastUploadSnapshot.memberInfo);
      setResult(lastUploadSnapshot.result);
      setFeedback(lastUploadSnapshot.feedback);
      setLastResultSource('upload');
    } else {
      setMemberInfo(null);
      setResult('');
      setFeedback(initialStatus);
      setLastResultSource(null);
    }
  }, [mode, lastUploadSnapshot]);

  useEffect(() => {
    if (mode !== 'scan') {
      return;
    }
    if (lastResultSource === 'upload') {
      setMemberInfo(null);
      setResult('');
      setFeedback(initialStatus);
      setLastResultSource(null);
    }
  }, [mode, lastResultSource]);

  const resolvedMemberPhoto = resolveBackendAssetUrl(memberInfo?.photo, { apiBaseUrl: API_BASE_URL });

  return (
    <div className={`scanqr-page scanqr-page--${mode}-mode${mode === 'scan' ? ' scanqr-page--scan-mode' : ''}`}>
      <header className="scanqr-page__header">
        <div className="scanqr-page__badge">
          <ShieldCheck size={18} />
          <span>LaborConnect Secure Verification</span>
        </div>
        <div>
          <h1>Scan Member QR Code</h1>
          <p>Verify membership by scanning a QR code or by uploading an existing screenshot.</p>
        </div>
      </header>

      <section className="scanqr-card">
        <div className="scanqr-card__tabs">
          <button
            type="button"
            className={mode === 'scan' ? 'active' : ''}
            onClick={() => {
              if (mode !== 'scan') {
                setMemberInfo(null);
                setResult('');
                setFeedback(initialStatus);
                setLastResultSource(null);
                setReferenceValue('');
                setLookupLoading(false);
              }
              setCameraActive(true);
              statusRef.current = 'idle';
              setStatus('idle');
              setMode('scan');
            }}
          >
            <Camera size={18} />
            Scan
          </button>
          <button
            type="button"
            className={mode === 'upload' ? 'active' : ''}
            onClick={() => {
              if (mode !== 'upload') {
                if (mode === 'scan') {
                  stopScanner('idle');
                  setCameraActive(false);
                }
                setMemberInfo(null);
                setResult('');
                setFeedback(initialStatus);
                setLastResultSource(null);
                setReferenceValue('');
                setLookupLoading(false);
              }
              setMode('upload');
            }}
          >
            <ImageUp size={18} />
            Upload
          </button>
          <button
            type="button"
            className={mode === 'reference' ? 'active' : ''}
            onClick={() => {
              if (mode !== 'reference') {
                if (mode === 'scan') {
                  stopScanner('idle');
                  setCameraActive(false);
                }
                setMemberInfo(null);
                setResult('');
                setFeedback(initialStatus);
                setLastResultSource(null);
              }
              setReferenceValue('');
              setLookupLoading(false);
              setMode('reference');
            }}
          >
            <Keyboard size={18} />
            Reference
          </button>
        </div>

        {mode === 'scan'
          ? (status === 'detected' && lastResultSource === 'scan'
            ? null
            : (
              <div className="scanqr-panel">
                <div className="scanqr-panel__video">
                  <div className={`scanqr-panel__status status-${status}`}>
                    <span />
                    <strong>
                      {status === 'preparing' && 'Preparing camera'}
                      {status === 'scanning' && 'Scanning for QR code'}
                      {status === 'detected' && 'QR code captured'}
                      {status === 'error' && 'Scanning unavailable'}
                      {status === 'idle' && 'Scanner idle'}
                    </strong>
                    <p>
                      {feedback.message || (
                        status === 'scanning'
                          ? 'Hold the QR code within the square. The scan stops once a code is detected.'
                          : 'Allow camera access to begin scanning.'
                      )}
                    </p>
                  </div>
                  <div className={`scanqr-panel__viewport${isMirrored ? ' mirrored' : ''}`}>
                    <video ref={videoRef} playsInline muted autoPlay />
                    <div className="scanqr-panel__frame">
                      <QrCode size={32} />
                    </div>
                  </div>
                  <div className="scanqr-panel__actions">
                    <button
                      type="button"
                      onClick={() => setIsMirrored((prev) => !prev)}
                    >
                      {isMirrored ? 'Unflip view' : 'Flip view'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (cameraActive) {
                          setCameraActive(false);
                          if (status === 'detected') {
                            stopScanner();
                          } else {
                            stopScanner('idle');
                            setStatus('idle');
                          }
                          if (lastResultSource !== 'scan') {
                            setMemberInfo(null);
                            setResult('');
                            setFeedback(initialStatus);
                          }
                        } else {
                          setFeedback(initialStatus);
                          setResult('');
                          statusRef.current = 'idle';
                          setStatus('idle');
                          setCameraActive(true);
                        }
                      }}
                    >
                      {cameraActive ? 'Turn off camera' : 'Turn on camera'}
                    </button>
                  </div>
                </div>
              </div>
            ))
          : (
          mode === 'upload'
            ? (
              <div className="scanqr-panel">
                <div className="scanqr-upload">
                  {!previewSrc && (
                    <label className="scanqr-upload__dropzone">
                      <input type="file" accept="image/*" onChange={handleUpload} />
                      <div>
                        <ImageUp size={28} />
                        <h3>Upload QR Code Image</h3>
                        <p>Drop an image here or click to choose a file.</p>
                      </div>
                    </label>
                  )}
                  {previewSrc && (
                    <div className="scanqr-upload__preview">
                      <img src={previewSrc} alt="Uploaded preview" />
                    </div>
                  )}
                  <div className="scanqr-panel__actions">
                    <button type="button" onClick={resetView}>Clear</button>
                  </div>
                </div>
              </div>
            )
            : (
              <div className="scanqr-panel">
                <form className="scanqr-reference" onSubmit={handleReferenceLookup}>
                  <div className="scanqr-reference__header">
                    <div className="scanqr-reference__icon">
                      <Keyboard size={20} />
                    </div>
                    <div>
                      <h3>Verify by Reference</h3>
                      <p>Paste the QR reference from the member’s Digital ID to confirm their status instantly.</p>
                    </div>
                  </div>
                  <div className="scanqr-reference__field">
                    <label htmlFor="qrReferenceInput">QR Reference Number</label>
                    <div className="scanqr-reference__input">
                      <span className="scanqr-reference__prefix">REF</span>
                      <input
                        id="qrReferenceInput"
                        type="text"
                        value={referenceValue}
                        onChange={(event) => setReferenceValue(event.target.value)}
                        placeholder="Example: QR-25242068"
                        autoComplete="off"
                        inputMode="text"
                      />
                    </div>
                    <p className="scanqr-reference__hint">The reference is shown under the QR code on the member’s digital ID card.</p>
                  </div>
                  <div className="scanqr-reference__actions scanqr-panel__actions">
                    <button type="button" onClick={resetView}>Clear</button>
                    <button type="submit" disabled={lookupLoading}>
                      {lookupLoading ? 'Checking...' : 'Verify'}
                    </button>
                  </div>
                </form>
              </div>
            )
        )}

        {(memberInfo || result || feedback.message || feedback.detail) && (
          <div className="scanqr-result">
            {memberInfo && (mode !== 'scan' || lastResultSource === 'scan') && (
              <div className="scanqr-result__member">
                <div className="scanqr-result__member-photo">
                  {resolvedMemberPhoto ? (
                    <img src={resolvedMemberPhoto} alt={memberInfo.name} />
                  ) : (
                    <span>{getInitials(memberInfo.name)}</span>
                  )}
                </div>
                <div className="scanqr-result__member-details">
                  <h3>{memberInfo.name}</h3>
                  <div className="scanqr-result__member-meta">
                    <div>
                      <span>ID Number</span>
                      <strong>{memberInfo.memberId}</strong>
                    </div>
                    <div>
                      <span>Union</span>
                      <strong>{memberInfo.union}</strong>
                    </div>
                    <div>
                      <span>Company</span>
                      <strong>{memberInfo.company}</strong>
                    </div>
                    {memberInfo.qrReference ? (
                      <div>
                        <span>QR Reference</span>
                        <strong>{memberInfo.qrReference}</strong>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            {/* Active Bookings Section */}
            {memberInfo && (mode !== 'scan' || lastResultSource === 'scan') && (
              <div style={{ marginTop: '32px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '20px',
                  paddingBottom: '12px',
                  borderBottom: '2px solid #e5ecf5'
                }}>
                  <CalendarCheck size={24} style={{ color: '#1d4ed8' }} />
                  <h3 style={{
                    margin: 0,
                    fontSize: '20px',
                    fontWeight: 700,
                    color: '#0f1b3d'
                  }}>
                    Active Venue Bookings
                  </h3>
                </div>

                {loadingBookings ? (
                  <div style={{
                    padding: '32px',
                    textAlign: 'center',
                    background: 'white',
                    borderRadius: '16px',
                    border: '1px solid #d8e0f0'
                  }}>
                    <p style={{ color: '#5b6c8f' }}>Loading bookings...</p>
                  </div>
                ) : bookingsError ? (
                  <div style={{
                    padding: '20px',
                    background: 'rgba(220, 38, 38, 0.05)',
                    border: '1px solid rgba(220, 38, 38, 0.2)',
                    borderRadius: '12px',
                    color: '#dc2626',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <AlertCircle size={20} />
                    <span>{bookingsError}</span>
                  </div>
                ) : activeBookings.length === 0 ? (
                  <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    background: 'white',
                    borderRadius: '16px',
                    border: '1px solid #d8e0f0'
                  }}>
                    <Calendar size={48} style={{ color: '#9ca3af', marginBottom: '16px' }} />
                    <p style={{ color: '#5b6c8f', margin: 0 }}>
                      No active venue bookings found
                    </p>
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gap: '16px'
                  }}>
                    {activeBookings.map((booking) => (
                      <div
                        key={booking.id}
                        style={{
                          background: 'white',
                          borderRadius: '16px',
                          border: '1px solid #d8e0f0',
                          padding: '20px',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                        }}
                      >
                        {/* Venue Name */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          marginBottom: '16px'
                        }}>
                          <MapPin size={20} style={{ color: '#1d4ed8', flexShrink: 0 }} />
                          <h4 style={{
                            margin: 0,
                            fontSize: '18px',
                            fontWeight: 700,
                            color: '#0f1b3d'
                          }}>
                            {booking.venueName}
                          </h4>
                        </div>

                        {/* Booking Details Grid */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: '16px',
                          marginBottom: '12px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Calendar size={16} style={{ color: '#5b6c8f' }} />
                            <div>
                              <span style={{ fontSize: '12px', color: '#9ca3af', display: 'block' }}>
                                Date
                              </span>
                              <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f1b3d' }}>
                                {new Date(booking.bookingDate).toLocaleDateString('en-PH', {
                                  month: 'short',
                                  day: '2-digit',
                                  year: 'numeric'
                                })}
                              </span>
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Clock size={16} style={{ color: '#5b6c8f' }} />
                            <div>
                              <span style={{ fontSize: '12px', color: '#9ca3af', display: 'block' }}>
                                Time
                              </span>
                              <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f1b3d' }}>
                                {formatTime(booking.startTime)} - {formatTime(booking.endTime)}
                              </span>
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Building2 size={16} style={{ color: '#5b6c8f' }} />
                            <div>
                              <span style={{ fontSize: '12px', color: '#9ca3af', display: 'block' }}>
                                Type
                              </span>
                              <span style={{
                                fontSize: '14px',
                                fontWeight: 500,
                                color: '#0f1b3d',
                                textTransform: 'capitalize'
                              }}>
                                {booking.venueType}
                              </span>
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 600,
                              background: booking.status === 'approved'
                                ? 'rgba(34, 197, 94, 0.1)'
                                : 'rgba(249, 115, 22, 0.1)',
                              color: booking.status === 'approved' ? '#16a34a' : '#f97316'
                            }}>
                              {booking.status === 'approved' ? 'Confirmed' : 'Pending Approval'}
                            </span>
                          </div>
                        </div>

                        {/* Booking ID */}
                        <div style={{
                          paddingTop: '12px',
                          borderTop: '1px solid #e5ecf5',
                          fontSize: '12px',
                          color: '#9ca3af'
                        }}>
                          Booking ID: #{booking.id}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Active Events Section */}
            {memberInfo && (mode !== 'scan' || lastResultSource === 'scan') && (
              <div style={{ marginTop: '32px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '20px',
                  paddingBottom: '12px',
                  borderBottom: '2px solid #e5ecf5'
                }}>
                  <CalendarRange size={24} style={{ color: '#1d4ed8' }} />
                  <h3 style={{
                    margin: 0,
                    fontSize: '20px',
                    fontWeight: 700,
                    color: '#0f1b3d'
                  }}>
                    Active Registered Events
                  </h3>
                </div>

                {loadingEvents ? (
                  <div style={{
                    padding: '32px',
                    textAlign: 'center',
                    background: 'white',
                    borderRadius: '16px',
                    border: '1px solid #d8e0f0'
                  }}>
                    <p style={{ color: '#5b6c8f' }}>Loading events...</p>
                  </div>
                ) : eventsError ? (
                  <div style={{
                    padding: '20px',
                    background: 'rgba(220, 38, 38, 0.05)',
                    border: '1px solid rgba(220, 38, 38, 0.2)',
                    borderRadius: '12px',
                    color: '#dc2626',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <AlertCircle size={20} />
                    <span>{eventsError}</span>
                  </div>
                ) : activeEvents.length === 0 ? (
                  <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    background: 'white',
                    borderRadius: '16px',
                    border: '1px solid #d8e0f0'
                  }}>
                    <Tent size={48} style={{ color: '#9ca3af', marginBottom: '16px' }} />
                    <p style={{ color: '#5b6c8f', margin: 0 }}>
                      No active event registrations found
                    </p>
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gap: '16px'
                  }}>
                    {activeEvents.map((evt) => (
                      <div
                        key={evt.id}
                        style={{
                          background: 'white',
                          borderRadius: '16px',
                          border: '1px solid #d8e0f0',
                          padding: '20px',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                        }}
                      >
                        {/* Event Title */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          marginBottom: '16px'
                        }}>
                          <Tent size={20} style={{ color: '#1d4ed8', flexShrink: 0 }} />
                          <h4 style={{
                            margin: 0,
                            fontSize: '18px',
                            fontWeight: 700,
                            color: '#0f1b3d'
                          }}>
                            {evt.title}
                          </h4>
                        </div>

                        {/* Event Details Grid */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: '16px',
                          marginBottom: '12px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Calendar size={16} style={{ color: '#5b6c8f' }} />
                            <div>
                              <span style={{ fontSize: '12px', color: '#9ca3af', display: 'block' }}>
                                Start Date
                              </span>
                              <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f1b3d' }}>
                                {new Date(evt.startAt).toLocaleDateString('en-PH', {
                                  month: 'short',
                                  day: '2-digit',
                                  year: 'numeric'
                                })}
                              </span>
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Clock size={16} style={{ color: '#5b6c8f' }} />
                            <div>
                              <span style={{ fontSize: '12px', color: '#9ca3af', display: 'block' }}>
                                Time
                              </span>
                              <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f1b3d' }}>
                                {new Date(evt.startAt).toLocaleTimeString('en-PH', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <MapPin size={16} style={{ color: '#5b6c8f' }} />
                            <div>
                              <span style={{ fontSize: '12px', color: '#9ca3af', display: 'block' }}>
                                Venue
                              </span>
                              <span style={{
                                fontSize: '14px',
                                fontWeight: 500,
                                color: '#0f1b3d',
                                textTransform: 'capitalize'
                              }}>
                                {evt.venue || 'TBA'}
                              </span>
                            </div>
                          </div>
                          
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 600,
                              background: evt.userAttendanceStatus === 'attended'
                                ? 'rgba(34, 197, 94, 0.1)'
                                : 'rgba(56, 182, 255, 0.1)',
                              color: evt.userAttendanceStatus === 'attended' ? '#16a34a' : '#0284c7',
                              textTransform: 'capitalize'
                            }}>
                              {evt.userAttendanceStatus || 'Registered'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!memberInfo && result && (mode !== 'scan' || lastResultSource === 'scan') && (
              <div className="scanqr-result__value">
                <span>Result</span>
                <p>{result}</p>
              </div>
            )}
            {(feedback.message || feedback.detail) && (
              <div className="scanqr-result__feedback">
                {feedback.message && <strong>{feedback.message}</strong>}
                {feedback.detail && <p>{feedback.detail}</p>}
              </div>
            )}
            {lastResultSource === 'scan' && status === 'detected' && (
              <div className="scanqr-result__actions scanqr-panel__actions">
                <button type="button" onClick={clearScanResult}>Scan another QR</button>
              </div>
            )}
          </div>
        )}
      </section>

      <canvas ref={canvasRef} className="scanqr-hidden-canvas" aria-hidden="true" />
    </div>
  );
}
